import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import * as _ from "lodash-es";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import DeliveryModeEnum from "../../../../models/DeliveryModeEnum";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IUpdateSupplierDTO from "../../../../models/DTO/IUpdateSupplierDTO";
import IOnUpdateSupplierDTO from "../../../../models/DTO/IOnUpdateSupplierDTO";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import { DAY_LABEL_KEYS } from "./deliveryDeadlineUtils";
import {
    DeliveryModeFormErrors,
    deliveryModeFormToSupplierChanges,
    getEarliestSelectableDate,
    IDeliveryModeFormState,
    MAX_DELIVERY_LEAD_DAYS,
    supplierToDeliveryModeForm,
    validateDeliveryModeForm,
} from "./deliveryDateRulesUtils";
import "./DeliveryModeSettings.scss";

type Props = {
    /** Called with the saved mode once the API confirms the save, so the page can show / hide the schedule sections. */
    onSaved?: (mode: DeliveryModeEnum) => void;
};

/**
 * Account delivery mode (OR-1531): "Fixed route (Delivery Deadlines)" vs "Customer selects date", and the
 * cutoff time / lead days / blocked days the latter applies at checkout. Saved through the standard
 * SupplierUpdateSupplier socket flow so the app-wide supplier model refreshes like every other setting.
 */
export const DeliveryModeSettings = (props: Props) => {
    const { userModel, supplierModel, uiContext, socket } = useAppContextNonNull();
    const intl = useIntl();

    const [form, setForm] = useState<IDeliveryModeFormState>(() => supplierToDeliveryModeForm(supplierModel));
    const [saved, setSaved] = useState<IDeliveryModeFormState>(() => supplierToDeliveryModeForm(supplierModel));
    const [errors, setErrors] = useState<DeliveryModeFormErrors>({});
    const [isSaving, setIsSaving] = useState(false);

    // The app reloads the supplier after a save; pick the stored values up so the form reflects them.
    useEffect(() => {
        const fromSupplier = supplierToDeliveryModeForm(supplierModel);
        setSaved(fromSupplier);
        setForm((current) => (_.isEqual(current, saved) ? fromSupplier : current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        supplierModel.delivery_mode,
        supplierModel.delivery_cutoff_time,
        supplierModel.delivery_lead_days,
        supplierModel.delivery_blocked_days,
    ]);

    useEffect(() => {
        const onUpdateSupplier = (data: IOnUpdateSupplierDTO) => {
            if (!isSaving) return;
            setIsSaving(false);
            const stored = data.supplierModel ? supplierToDeliveryModeForm(data.supplierModel) : form;
            setSaved(stored);
            setForm(stored);
            uiContext.showNotification({ message: "delivery_mode_saved", level: NotificationLevelEnum.success });
            props.onSaved?.(stored.delivery_mode);
        };
        const onNotify = (data?: IErrorDTO) => {
            if (!isSaving || data?.message !== "ERROR_UPDATING_SUPPLIER") return;
            setIsSaving(false);
        };

        socket.on(EndpointsEnum.SupplierOnUpdateSupplier, onUpdateSupplier);
        socket.on(EndpointsEnum.Notify, onNotify);
        return () => {
            socket.off(EndpointsEnum.SupplierOnUpdateSupplier, onUpdateSupplier);
            socket.off(EndpointsEnum.Notify, onNotify);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSaving, form]);

    const isCustomerSelects = form.delivery_mode === DeliveryModeEnum.CUSTOMER_SELECTS;
    const isDirty = !_.isEqual(form, saved);

    const setMode = (mode: DeliveryModeEnum) => setForm((prev) => ({ ...prev, delivery_mode: mode }));

    const toggleBlockedDay = (day: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((prev) => ({
            ...prev,
            delivery_blocked_days: e.target.checked
                ? Array.from(new Set([...prev.delivery_blocked_days, day])).sort((a, b) => a - b)
                : prev.delivery_blocked_days.filter((d) => d !== day),
        }));
    };

    const save = () => {
        const validationErrors = validateDeliveryModeForm(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const payload: IUpdateSupplierDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            supplierModel: deliveryModeFormToSupplierChanges(form),
        };

        setIsSaving(true);
        socket.emit(EndpointsEnum.SupplierUpdateSupplier, payload);
    };

    // Live example so the admin sees what a customer ordering right now would be offered.
    const previewEarliest =
        isCustomerSelects && Object.keys(validateDeliveryModeForm(form)).length === 0
            ? getEarliestSelectableDate(
                  {
                      cutoffTime: form.delivery_cutoff_time,
                      leadDays: Number(form.delivery_lead_days) || 0,
                      blockedDays: form.delivery_blocked_days,
                  },
                  supplierModel.timezone
              )
            : null;

    return (
        <div className="DeliveryModeSettings rounded p-3 mt-3">
            <OwLabel title="delivery_mode" className="fw-bold" />
            <OwLabel title="help_text_delivery_mode" className="text-muted small d-block mb-2" />

            <div className="form-check">
                <input
                    className="form-check-input"
                    type="radio"
                    name="delivery_mode"
                    id="delivery_mode_fixed_route"
                    checked={!isCustomerSelects}
                    onChange={() => setMode(DeliveryModeEnum.FIXED_ROUTE)}
                    disabled={isSaving}
                />
                <label className="form-check-label" htmlFor="delivery_mode_fixed_route">
                    <span className="fw-bold">{intl.formatMessage({ id: "delivery_mode_fixed_route" })}</span>
                    <span className="text-muted ms-1">{intl.formatMessage({ id: "delivery_mode_fixed_route_hint" })}</span>
                </label>
            </div>
            <div className="form-check">
                <input
                    className="form-check-input"
                    type="radio"
                    name="delivery_mode"
                    id="delivery_mode_customer_selects"
                    checked={isCustomerSelects}
                    onChange={() => setMode(DeliveryModeEnum.CUSTOMER_SELECTS)}
                    disabled={isSaving}
                />
                <label className="form-check-label" htmlFor="delivery_mode_customer_selects">
                    <span className="fw-bold">{intl.formatMessage({ id: "delivery_mode_customer_selects" })}</span>
                    <span className="text-muted ms-1">{intl.formatMessage({ id: "delivery_mode_customer_selects_hint" })}</span>
                </label>
            </div>

            {isCustomerSelects && (
                <div className="row g-3 mt-1">
                    <div className="col-md-3">
                        <OwLabel title="daily_cutoff_time" noBottomMargin={true} />
                        <input
                            type="time"
                            aria-label="daily cutoff time"
                            className="form-control form-control-sm"
                            value={form.delivery_cutoff_time}
                            onChange={(e) => setForm((prev) => ({ ...prev, delivery_cutoff_time: e.target.value }))}
                            disabled={isSaving}
                        />
                        {errors.delivery_cutoff_time && <OwLabel title={errors.delivery_cutoff_time} className="text-danger small" />}
                        <OwLabel title="daily_cutoff_time_helper" className="text-muted small" />
                    </div>
                    <div className="col-md-3">
                        <OwLabel title="lead_time_days" noBottomMargin={true} />
                        <input
                            type="number"
                            min="0"
                            max={MAX_DELIVERY_LEAD_DAYS}
                            step="1"
                            inputMode="numeric"
                            aria-label="lead time days"
                            className="form-control form-control-sm"
                            value={form.delivery_lead_days}
                            onChange={(e) => setForm((prev) => ({ ...prev, delivery_lead_days: e.target.value }))}
                            disabled={isSaving}
                        />
                        {errors.delivery_lead_days && <OwLabel title={errors.delivery_lead_days} className="text-danger small" />}
                        <OwLabel title="lead_time_days_helper" className="text-muted small" />
                    </div>
                    <div className="col-md-6">
                        <OwLabel title="blocked_days_of_week" noBottomMargin={true} />
                        <div className="d-flex flex-column gap-1">
                            {DAY_LABEL_KEYS.map((key, day) => (
                                <OwCheckbox
                                    key={key}
                                    name={`blocked_day_${day}`}
                                    label={key}
                                    checked={form.delivery_blocked_days.includes(day)}
                                    fn={toggleBlockedDay(day)}
                                    disabled={isSaving}
                                />
                            ))}
                        </div>
                        {errors.delivery_blocked_days && <OwLabel title={errors.delivery_blocked_days} className="text-danger small" />}
                        <OwLabel title="blocked_days_of_week_helper" className="text-muted small" />
                    </div>
                    {previewEarliest && (
                        <div className="col-12 small">
                            <span className="fw-bold">{intl.formatMessage({ id: "earliest_delivery_date_if_ordered_now" })}: </span>
                            <span>
                                {intl.formatDate(new Date(`${previewEarliest}T12:00:00`), {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                })}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="mt-3 d-flex gap-2">
                <OwButton title="save" fn={save} className="btn-primary btn-sm" disabled={isSaving || !isDirty} />
                <OwButton
                    title="cancel"
                    fn={() => {
                        setForm(saved);
                        setErrors({});
                    }}
                    className="btn-secondary btn-sm"
                    disabled={isSaving || !isDirty}
                />
            </div>
        </div>
    );
};

export default DeliveryModeSettings;
